import React, { useCallback, useState } from 'react';
import { format } from 'date-fns';
import { range } from 'lodash-es';

import {
  AdornedInput,
  Box,
  DateInput,
  DatePicker,
  Divider,
  FormField,
  FormHelp,
  Input,
  Label,
  Paragraph,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import { IconPlusFilled } from '@zillow/constellation-icons';

import { AdditionalOccupantSection, Field } from '../../../models/index';
import { ADDITIONAL_OCCUPANT } from '../../../constants/index';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';
import { stopPropagationIfInvalidNumber } from '../../../utils/validatorTypes/PositiveIntegerValidator';
import { FULLSTORY_EXCLUDE } from '../../../constants/analytics';
import { isYesNoEnabledForNumberedFields } from '../../../constants/functionConstants';
import { DATE_PICKER_FORMAT } from '../../../constants/stringConstants';
import { useIndexedSection } from '../../../hooks';

export const MAXIMUM_ADDITIONAL_OCCUPANTS = 5;

type Props = {
  section: AdditionalOccupantSection;
  setSectionValues: (keyValuePairs: object) => void;
  setSection: (section: AdditionalOccupantSection) => void;
};

export default function AdditionalOccupant({ section, setSectionValues, setSection }: Props): JSX.Element {
  const [hasClickedNo, setHasClickedNo] = useState(false);

  const { onAddOne, onDeleteOne } = useIndexedSection({
    section,
    setSection,
    SectionClass: AdditionalOccupantSection,
  });

  function onRemoveAll(): void {
    setHasClickedNo(true);
    const updatedSection = section.clone();
    updatedSection.removeAllIndexes();
    setSection(updatedSection);
  }

  function setFirstName(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.FIRST_NAME, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function setLastName(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.LAST_NAME, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function setAge(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.AGE, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  const setBirthdate = useCallback(
    (index: number, value: Date | string): void => {
      const formatted = typeof value === 'string' ? value : format(value, DATE_PICKER_FORMAT);
      const fieldName = Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.BIRTH_DATE, Number(index));
      setSectionValues({ [fieldName]: formatted });
    },
    [setSectionValues],
  );

  function renderAdditionalOccupant(index: number): React.ReactNode {
    const firstNameField = section.getField(
      Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.FIRST_NAME, index),
    );
    const lastNameField = section.getField(Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.LAST_NAME, index));
    const ageField = section.getField(Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.AGE, index));
    const birthdateField = section.getField(
      Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.BIRTH_DATE, index),
    );

    return (
      <Box data-index={`occupant ${index}`} data-test="occupant">
        {index > 0 && (
          <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text textStyle="body-bold" id="additional-occupant">
              Additional occupant #{index + 1}
            </Text>
            <TextButton data-index={index} data-test="delete-occupant" onClick={onDeleteOne}>
              Delete
            </TextButton>
          </Box>
        )}
        <Box
          className={FULLSTORY_EXCLUDE}
          css={{
            display: 'grid',
            gridTemplateColumns: 'repeat(12, 1fr)',
            gap: 'layout.tighter',
            marginBottom: 'layout.tight',
          }}
        >
          <Box css={{ gridColumnEnd: { base: 'span 12', sm: 'span 6' } }}>
            <FormField
              required
              error={!!firstNameField.showOnError(ADDITIONAL_OCCUPANT.ERROR_MESSAGE.NAME)}
              label={<Label id="first-name">First name</Label>}
              control={
                <Input
                  data-index={index}
                  data-test="first-name"
                  value={firstNameField.enteredValue}
                  error={!!firstNameField.showOnError(ADDITIONAL_OCCUPANT.ERROR_MESSAGE.NAME)}
                  onChange={setFirstName}
                />
              }
              description={
                firstNameField.showOnError(ADDITIONAL_OCCUPANT.ERROR_MESSAGE.NAME) && (
                  <FormHelp>{firstNameField.showOnError(ADDITIONAL_OCCUPANT.ERROR_MESSAGE.NAME)}</FormHelp>
                )
              }
            />
          </Box>
          <Box css={{ gridColumnEnd: { base: 'span 12', sm: 'span 6' } }}>
            <FormField
              required
              css={{ marginBottom: 'layout.tighter' }}
              error={!!lastNameField.showOnError(ADDITIONAL_OCCUPANT.ERROR_MESSAGE.NAME)}
              label={<Label id="last-name">Last name</Label>}
              control={
                <Input
                  data-index={index}
                  data-test="last-name"
                  value={lastNameField.enteredValue}
                  error={!!lastNameField.showOnError(ADDITIONAL_OCCUPANT.ERROR_MESSAGE.NAME)}
                  onChange={setLastName}
                />
              }
              description={
                lastNameField.showOnError(ADDITIONAL_OCCUPANT.ERROR_MESSAGE.NAME) && (
                  <FormHelp>{lastNameField.showOnError(ADDITIONAL_OCCUPANT.ERROR_MESSAGE.NAME)}</FormHelp>
                )
              }
            />
          </Box>
          {Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.AGE, index) &&
            section.getField(Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.AGE, index)) && (
              <Box css={{ gridColumnEnd: 'span 12' }}>
                <FormField
                  required
                  error={!!ageField.showOnError(ADDITIONAL_OCCUPANT.ERROR_MESSAGE.AGE)}
                  label={<Label id="age">Age</Label>}
                  control={
                    <AdornedInput.Root error={!!ageField.showOnError(ADDITIONAL_OCCUPANT.ERROR_MESSAGE.AGE)}>
                      <AdornedInput.Input
                        aria-labelledby="additional occupant age"
                        data-index={index}
                        data-test="age"
                        onKeyDown={stopPropagationIfInvalidNumber}
                        type="number"
                        value={ageField.enteredValue || ''}
                        onChange={setAge}
                      />
                      <AdornedInput.Adornment>years old</AdornedInput.Adornment>
                    </AdornedInput.Root>
                  }
                  description={
                    ageField.showOnError(ADDITIONAL_OCCUPANT.ERROR_MESSAGE.AGE) && (
                      <FormHelp>{ageField.showOnError(ADDITIONAL_OCCUPANT.ERROR_MESSAGE.AGE)}</FormHelp>
                    )
                  }
                />
              </Box>
            )}
          {Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.BIRTH_DATE, index) &&
            section.getField(Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.BIRTH_DATE, index)) && (
              <Box css={{ gridColumnEnd: 'span 12' }}>
                <FormField
                  required
                  label={<Label id="age">Birthdate</Label>}
                  control={
                    <DatePicker
                      dateFormat={DATE_PICKER_FORMAT}
                      minDate={null}
                      value={birthdateField.enteredValue}
                      onValueChange={(value) => setBirthdate(index, value)}
                      maxDate={new Date()}
                      closeModalOnDateSelect
                      input={<DateInput />}
                    />
                  }
                />
              </Box>
            )}
        </Box>
      </Box>
    );
  }

  const totalAdditionalOccupants = section.getTotalIndexes();
  const isEnabled = isYesNoEnabledForNumberedFields(section, 'additionalOccupant0_isEnabled', hasClickedNo);

  if (totalAdditionalOccupants == null) {
    return null;
  }

  return (
    <FormField
      required
      label={
        <>
          <Label>Will there be any additional occupants?</Label>
          <Paragraph css={{ marginBottom: 'layout.tight' }}>
            Occupants should only be people who aren’t paying tenants such as children, elderly relatives, and other
            dependents.
          </Paragraph>
        </>
      }
      control={
        <YesAndNoExpansion yesHandler={onAddOne} noHandler={onRemoveAll} yesActive={isEnabled}>
          {range(0, totalAdditionalOccupants).map((index: number) => (
            <React.Fragment key={index}>
              {index > 0 && <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />}
              {renderAdditionalOccupant(index)}
            </React.Fragment>
          ))}
          {totalAdditionalOccupants < MAXIMUM_ADDITIONAL_OCCUPANTS ? (
            totalAdditionalOccupants > 0 && (
              <TextButton className="add-another" icon={<IconPlusFilled />} data-test="add-another" onClick={onAddOne}>
                Add another occupant
              </TextButton>
            )
          ) : (
            <Text data-test="maximum-reached">
              You’ve added {MAXIMUM_ADDITIONAL_OCCUPANTS} additional occupants, which is the maximum allowed.
            </Text>
          )}
        </YesAndNoExpansion>
      }
    />
  );
}
